import { Countdown } from '../countdown';
import { FormatDate } from '../format/format-date';
import { ViewDateIcon, ViewDisputeIcon, ViewFinalIcon, ViewProposedIcon } from '@/client/assets';
import { cn } from '@/client/utils';
import { MarketStatus } from '@/libs/sdk/api';
import type { Time } from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type MarketDeadlineProps = {
  className?: string;
  deadline?: number;
  status?: MarketStatus;
  formatStr?: string;
};

export const MarketDeadline = ({ className, deadline, status, formatStr = 'MMM d, yyyy' }: MarketDeadlineProps) => {
  const { t } = useTranslation();
  const [intervalTime, setIntervalTime] = useState(Number(deadline) - Math.ceil(Date.now() / 1e3));
  const isCountdown = intervalTime > 0 && intervalTime <= 24 * 3600;
  const ref = useRef<number>();
  useEffect(() => {
    if (!isCountdown) return;
    ref.current = setInterval(() => {
      setIntervalTime(Number(deadline) - Math.ceil(Date.now() / 1e3));
    }, 1000) as any;
    return () => {
      clearInterval(ref.current);
    };
  }, [deadline, intervalTime, isCountdown]);

  if (status == MarketStatus.settlement) {
    return (
      <div className="flex space-x-1 items-center">
        <ViewProposedIcon className="size-4 fill-foreground/40" />
        <span className="whitespace-nowrap text-foreground">{t('outcome-proposed')}</span>
      </div>
    );
  }
  if (status == MarketStatus.disputed) {
    return (
      <div className="flex space-x-1 items-center">
        <ViewDisputeIcon className="size-4 fill-foreground/40" />
        <span className="whitespace-nowrap text-foreground">{t('dispute-window')}</span>
      </div>
    );
  }
  if (status == MarketStatus.closed) {
    return (
      <div className="flex space-x-1 items-center">
        <ViewFinalIcon className="size-4 fill-foreground/40" />
        <span className="whitespace-nowrap text-foreground">{t('final-outcome')}</span>
      </div>
    );
  }
  // Two formats only (QA 7/23): a live countdown inside the last 24h, an
  // absolute date otherwise. The old 1-3 day relative "in 2 days" middle tier
  // read as a third format and its day-rounding clashed with dates already
  // spelled out in market titles. Both get an explicit "Ends" label so the
  // deadline isn't mistaken for the title's observation-window date.
  return (
    <div className={cn('flex items-center space-x-1', className)}>
      <ViewDateIcon className="size-4 fill-current" />
      {/* nowrap: in CJK locales the label + date otherwise breaks per
          character when the card footer runs tight (400px zh) */}
      <span className="whitespace-nowrap">
        {isCountdown ? (
          <>
            {t('ends-in', 'Ends in')} <Countdown seconds={intervalTime} />
          </>
        ) : (
          <>
            {t('ends-label', 'Ends')} <FormatDate time={Number(deadline) as Time} formatStr={formatStr} />
          </>
        )}
      </span>
    </div>
  );
};
